"use client"

import type React from "react"

import { useRef, useState } from "react"
import { motion, useInView } from "framer-motion"
import { Send, MapPin, Phone, Mail, Clock } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useRouter } from 'next/navigation';

// Phone validation utility
const countryCodes = [
  { 
    code: "US", 
    name: "United States", 
    dial: "+1",
    placeholder: "(555) 123-4567",
    format: "(XXX) XXX-XXXX",
    minLength: 10,
    maxLength: 10,
    pattern: /^[\d\s\-\(\)]+$/
  },
  { 
    code: "IN", 
    name: "India", 
    dial: "+91",
    placeholder: "98765 43210",
    format: "XXXXX XXXXX",
    minLength: 10,
    maxLength: 10,
    pattern: /^[\d\s]+$/
  },
  { 
    code: "GB", 
    name: "United Kingdom", 
    dial: "+44",
    placeholder: "7911 123456",
    format: "XXXX XXXXXX",
    minLength: 10,
    maxLength: 11,
    pattern: /^[\d\s]+$/
  },
  { 
    code: "AU", 
    name: "Australia", 
    dial: "+61",
    placeholder: "412 345 678",
    format: "XXX XXX XXX",
    minLength: 9,
    maxLength: 9,
    pattern: /^[\d\s]+$/
  },
  { 
    code: "DE", 
    name: "Germany", 
    dial: "+49",
    placeholder: "151 12345678",
    format: "XXX XXXXXXXX",
    minLength: 10,
    maxLength: 12,
    pattern: /^[\d\s]+$/
  },
  { 
    code: "FR", 
    name: "France", 
    dial: "+33",
    placeholder: "06 12 34 56 78",
    format: "XX XX XX XX XX",
    minLength: 10,
    maxLength: 10,
    pattern: /^[\d\s]+$/
  },
  { 
    code: "JP", 
    name: "Japan", 
    dial: "+81",
    placeholder: "90-1234-5678",
    format: "XX-XXXX-XXXX",
    minLength: 10,
    maxLength: 11,
    pattern: /^[\d\-]+$/
  },
  { 
    code: "CN", 
    name: "China", 
    dial: "+86",
    placeholder: "138 0013 8000",
    format: "XXX XXXX XXXX",
    minLength: 11,
    maxLength: 11,
    pattern: /^[\d\s]+$/
  },
  { 
    code: "CA", 
    name: "Canada", 
    dial: "+1",
    placeholder: "(416) 555-1234",
    format: "(XXX) XXX-XXXX",
    minLength: 10,
    maxLength: 10,
    pattern: /^[\d\s\-\(\)]+$/
  },
  { 
    code: "BR", 
    name: "Brazil", 
    dial: "+55",
    placeholder: "(11) 99999-1234",
    format: "(XX) XXXXX-XXXX",
    minLength: 10,
    maxLength: 11,
    pattern: /^[\d\s\-\(\)]+$/
  }
];

// Phone validation function
const validatePhoneNumber = (phoneNumber: string, countryCode: string) => {
  const country = countryCodes.find(c => c.dial === countryCode);
  if (!country) return { isValid: false, error: "Invalid country code" };
  
  // Remove all non-digit characters for length validation
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Check if empty
  if (!phoneNumber.trim()) {
    return { isValid: true, error: null }; // Allow empty for optional fields
  }
  
  // Check pattern
  if (!country.pattern.test(phoneNumber)) {
    return { isValid: false, error: "Invalid characters in phone number" };
  }
  
  // Check length
  if (digitsOnly.length < country.minLength) {
    return { isValid: false, error: `Phone number too short. Minimum ${country.minLength} digits required.` };
  }
  
  if (digitsOnly.length > country.maxLength) {
    return { isValid: false, error: `Phone number too long. Maximum ${country.maxLength} digits allowed.` };
  }
  
  return { isValid: true, error: null };
};

// Format phone number according to country format
const formatPhoneNumber = (phoneNumber: string, countryCode: string) => {
  const country = countryCodes.find(c => c.dial === countryCode);
  if (!country) return phoneNumber;
  
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Limit digits to maxLength
  const limitedDigits = digitsOnly.slice(0, country.maxLength);
  
  // Special formatting for each country
  switch (countryCode) {
    case '+1': // US/Canada
      if (limitedDigits.length === 0) return '';
      if (limitedDigits.length <= 3) return `(${limitedDigits}`;
      if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
      return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
      
    case '+91': // India
      if (limitedDigits.length === 0) return '';
      if (limitedDigits.length <= 5) return limitedDigits;
      return `${limitedDigits.slice(0, 5)} ${limitedDigits.slice(5)}`;
      
    case '+44': // UK
      if (limitedDigits.length === 0) return '';
      if (limitedDigits.length <= 4) return limitedDigits;
      return `${limitedDigits.slice(0, 4)} ${limitedDigits.slice(4)}`;
      
    case '+61': // Australia
      if (limitedDigits.length === 0) return '';
      if (limitedDigits.length <= 3) return limitedDigits;
      if (limitedDigits.length <= 6) return `${limitedDigits.slice(0, 3)} ${limitedDigits.slice(3)}`;
      return `${limitedDigits.slice(0, 3)} ${limitedDigits.slice(3, 6)} ${limitedDigits.slice(6)}`;
      
    case '+49': // Germany
      if (limitedDigits.length === 0) return '';
      if (limitedDigits.length <= 3) return limitedDigits;
      return `${limitedDigits.slice(0, 3)} ${limitedDigits.slice(3)}`;
      
    case '+33': // France
      if (limitedDigits.length === 0) return '';
      let formatted = '';
      for (let i = 0; i < limitedDigits.length; i += 2) {
        if (i > 0) formatted += ' ';
        formatted += limitedDigits.slice(i, i + 2);
      }
      return formatted;
      
    case '+81': // Japan
      if (limitedDigits.length === 0) return '';
      if (limitedDigits.length <= 2) return limitedDigits;
      if (limitedDigits.length <= 6) return `${limitedDigits.slice(0, 2)}-${limitedDigits.slice(2)}`;
      return `${limitedDigits.slice(0, 2)}-${limitedDigits.slice(2, 6)}-${limitedDigits.slice(6)}`;
      
    case '+86': // China
      if (limitedDigits.length === 0) return '';
      if (limitedDigits.length <= 3) return limitedDigits;
      if (limitedDigits.length <= 7) return `${limitedDigits.slice(0, 3)} ${limitedDigits.slice(3)}`;
      return `${limitedDigits.slice(0, 3)} ${limitedDigits.slice(3, 7)} ${limitedDigits.slice(7)}`;
      
    case '+55': // Brazil
      if (limitedDigits.length === 0) return '';
      if (limitedDigits.length <= 2) return `(${limitedDigits}`;
      if (limitedDigits.length <= 7) return `(${limitedDigits.slice(0, 2)}) ${limitedDigits.slice(2)}`;
      return `(${limitedDigits.slice(0, 2)}) ${limitedDigits.slice(2, 7)}-${limitedDigits.slice(7)}`;
      
    default:
      return limitedDigits;
  }
};

// Get placeholder for selected country
const getPlaceholder = (countryCode: string) => {
  const country = countryCodes.find(c => c.dial === countryCode);
  return country ? country.placeholder : "Enter phone number";
};

export default function ContactSection() {
  const router = useRouter();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    category: "",
    message: "",
    countryCode: "+91",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [phoneError, setPhoneError] = useState("")
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(sectionRef, { once: false, amount: 0.2, margin: "-100px" })

  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 1, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSelectChange = (value: string) => {
    setFormData((prev) => ({ ...prev, category: value }))
  }

  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    setFormData((prev) => ({
      ...prev,
      countryCode: code,
      phone: "" // Clear phone when country changes
    }));
    setPhoneError(""); // Clear any existing error
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const country = countryCodes.find(c => c.dial === formData.countryCode);
    
    if (!country) return;
    
    // Only allow digits and valid formatting characters for the country
    const allowedChars = country.pattern;
    if (!allowedChars.test(inputValue) && inputValue !== "") {
      return; // Don't update if invalid characters
    }
    
    // Extract digits and limit to maxLength
    const digitsOnly = inputValue.replace(/\D/g, '');
    if (digitsOnly.length > country.maxLength) {
      return; // Don't update if exceeds max length
    }
    
    // Auto-format the phone number
    const formatted = formatPhoneNumber(inputValue, formData.countryCode);
    setFormData((prev) => ({ ...prev, phone: formatted }));
    
    // Validate and set error
    const validation = validatePhoneNumber(formatted, formData.countryCode);
    setPhoneError(validation.isValid ? "" : validation.error || "");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate phone number before submission
    if (formData.phone) {
      const validation = validatePhoneNumber(formData.phone, formData.countryCode);
      if (!validation.isValid) {
        setPhoneError(validation.error || "Invalid phone number");
        return;
      }
    }
    
    setIsSubmitting(true)

    try {
      const res = await fetch("/api/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          phone: formData.phone ? `${formData.countryCode} ${formData.phone}` : "",
        }),
      })

      const result = await res.json()
      if (result.success) {
        setIsSuccess(true)
        router.push('/thank-you'); // Redirect to thank you page

        setTimeout(() => {
          setFormData({ 
            name: "", 
            email: "", 
            phone: "", 
            company: "", 
            category: "", 
            message: "", 
            countryCode: "+91" 
          })
          setIsSuccess(false)
          setPhoneError("")
        }, 2000)
      } else {
        alert("Failed to send email. Please try again.")
      }
    } catch (err) {
      console.error("Email error", err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const reportCategories = [
    { value: "Annual Reports", label: "Annual Reports" },
    { value: "Integrated Reports", label: "Integrated Reports" },
    { value: "Sustainability Reports", label: "Sustainability Reports" },
    { value: "CSR Reports", label: "CSR Reports" },
    { value: "Digital Reports", label: "Digital Reports" },
    { value: "Investor Presentations", label: "Investor Presentations" },
    { value: "Creative Reports", label: "Creative Reports" },
  ]

  return (
    <section
      ref={sectionRef}
      className="py-16 md:py-20 lg:py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
      id="contact"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-dots-pattern opacity-5"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-6xl">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-8 md:mb-16"
        >
          {/* <motion.h2
            variants={itemVariants}
            className="inline-block mb-3 md:mb-4 px-4 py-2 rounded-full bg-gray-200 text-gray-800 font-medium text-sm"
          >
            Contact Us
          </motion.h2> */}

          <motion.h2
            variants={itemVariants}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4"
          >
            Contact Us
          </motion.h2>

          <motion.p variants={itemVariants} className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
            Let's discuss how we can help transform your corporate reporting and create impactful designs that resonate
            with your stakeholders.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
          className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 items-start"
        >
          {/* Contact Form - 3 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-3">
            <motion.div
              className="bg-white rounded-xl md:rounded-3xl shadow-lg p-6 md:p-8 lg:p-10 relative overflow-hidden border"
              whileHover={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)" }}
            >
              {/* Decorative elements */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-gray-200/50 rounded-full"></div>
              <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gray-300/30 rounded-full"></div>

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
                      placeholder="Your name"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
                      placeholder="your.email@company.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                      Phone Number
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.countryCode}
                        onChange={handleCountryCodeChange}
                        className="bg-gray-50 border border-gray-300 rounded-lg md:rounded-xl h-12 px-3 min-w-[100px]"
                      >
                        {countryCodes.map((country) => (
                          <option key={country.code} value={country.dial}>
                            {country.dial} {country.code}
                          </option>
                        ))}
                      </select>

                      <div className="flex-1">
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          className={`bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12 ${
                            phoneError ? 'border-red-500 focus:border-red-500' : ''
                          }`}
                          placeholder={getPlaceholder(formData.countryCode)}
                        />
                      </div>
                    </div>
                    {phoneError && (
                      <p className="text-red-500 text-sm mt-1">{phoneError}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                      Company
                    </label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
                      placeholder="Your company name"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="category" className="block text-gray-700 font-medium mb-2">
                    Report Type
                  </label>
                  <Select value={formData.category} onValueChange={handleSelectChange}>
                    <SelectTrigger className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12">
                      <SelectValue placeholder="Select a report type" />
                    </SelectTrigger>
                    <SelectContent className="rounded-lg md:rounded-xl">
                      {reportCategories.map((category) => (
                        <SelectItem key={category.value} value={category.value}>
                          {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl resize-none"
                    placeholder="Tell us about your project and requirements..."
                    required
                  />
                </div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <button
                    type="submit"
                    disabled={isSubmitting || isSuccess || !!phoneError}
                    className={`w-full rounded-lg md:rounded-xl py-4 md:py-6 text-base md:text-lg font-medium flex items-center justify-center gap-2 transition-all duration-300 ${
                      isSuccess 
                        ? "bg-green-500 hover:bg-green-600" 
                        : phoneError 
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gray-800 hover:bg-gray-900"
                    } text-white`}
                  >
                    {isSubmitting ? (
                      <>
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <span>Sending...</span>
                      </>
                    ) : isSuccess ? (
                      <>
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Message Sent!</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </motion.div>
              </form>
            </motion.div>
          </motion.div>

          {/* Contact Information - 2 columns */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <div className="bg-gray-800 text-white rounded-xl md:rounded-3xl shadow-lg p-6 md:p-8 h-full">
              <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-white">Contact Information</h3>

              <div className="space-y-6 md:space-y-8">
                <div className="flex items-start">
                  <div className="bg-white/20 p-3 rounded-full mr-4">
                    <MapPin className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-2">Our Address</h4>
                    <p className="text-gray-300 text-sm md:text-base">
                      203, BSI Business Park, H-160, H Block, Sector 63, Noida, Uttar Pradesh 201301, India.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-white/20 p-3 rounded-full mr-4">
                    <Phone className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-2">Call Us</h4>
                    <a href="tel:+919411953348" className="text-lg md:text-xl font-bold text-white">+91 94119 53348 (India)</a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-white/20 p-3 rounded-full mr-4">
                    <Mail className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-2">Email Us</h4>
                    <a href="mailto:contact@annualesg.com" className="text-lg md:text-xl font-bold text-white">contact@annualesg.com</a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-white/20 p-3 rounded-full mr-4">
                    <Clock className="w-5 h-5 md:w-6 md:h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium mb-2">Business Hours</h4>
                    <p className="text-gray-300 text-sm md:text-base">Monday - Saturday, 9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}




// "use client"

// import type React from "react"

// import { useRef, useState } from "react"
// import { motion, useInView } from "framer-motion"
// import { Send, MapPin, Phone, Mail, Clock } from "lucide-react"
// import { Input } from "@/components/ui/input"
// import { Textarea } from "@/components/ui/textarea"
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
// import { useRouter } from 'next/navigation';

// // Phone validation utility
// const countryCodes = [
//   { 
//     code: "US", 
//     name: "United States", 
//     dial: "+1",
//     placeholder: "(555) 123-4567",
//     format: "(XXX) XXX-XXXX",
//     minLength: 10,
//     maxLength: 10,
//     pattern: /^[\d\s\-\(\)]+$/
//   },
//   { 
//     code: "IN", 
//     name: "India", 
//     dial: "+91",
//     placeholder: "98765 43210",
//     format: "XXXXX XXXXX",
//     minLength: 10,
//     maxLength: 10,
//     pattern: /^[\d\s]+$/
//   },
//   { 
//     code: "GB", 
//     name: "United Kingdom", 
//     dial: "+44",
//     placeholder: "7911 123456",
//     format: "XXXX XXXXXX",
//     minLength: 10,
//     maxLength: 11,
//     pattern: /^[\d\s]+$/
//   },
//   { 
//     code: "AU", 
//     name: "Australia", 
//     dial: "+61",
//     placeholder: "412 345 678",
//     format: "XXX XXX XXX",
//     minLength: 9,
//     maxLength: 9,
//     pattern: /^[\d\s]+$/
//   },
//   { 
//     code: "DE", 
//     name: "Germany", 
//     dial: "+49",
//     placeholder: "151 12345678",
//     format: "XXX XXXXXXXX",
//     minLength: 10,
//     maxLength: 12,
//     pattern: /^[\d\s]+$/
//   },
//   { 
//     code: "FR", 
//     name: "France", 
//     dial: "+33",
//     placeholder: "06 12 34 56 78",
//     format: "XX XX XX XX XX",
//     minLength: 10,
//     maxLength: 10,
//     pattern: /^[\d\s]+$/
//   },
//   { 
//     code: "JP", 
//     name: "Japan", 
//     dial: "+81",
//     placeholder: "90-1234-5678",
//     format: "XX-XXXX-XXXX",
//     minLength: 10,
//     maxLength: 11,
//     pattern: /^[\d\-]+$/
//   },
//   { 
//     code: "CN", 
//     name: "China", 
//     dial: "+86",
//     placeholder: "138 0013 8000",
//     format: "XXX XXXX XXXX",
//     minLength: 11,
//     maxLength: 11,
//     pattern: /^[\d\s]+$/
//   },
//   { 
//     code: "CA", 
//     name: "Canada", 
//     dial: "+1",
//     placeholder: "(416) 555-1234",
//     format: "(XXX) XXX-XXXX",
//     minLength: 10,
//     maxLength: 10,
//     pattern: /^[\d\s\-\(\)]+$/
//   },
//   { 
//     code: "BR", 
//     name: "Brazil", 
//     dial: "+55",
//     placeholder: "(11) 99999-1234",
//     format: "(XX) XXXXX-XXXX",
//     minLength: 10,
//     maxLength: 11,
//     pattern: /^[\d\s\-\(\)]+$/
//   }
// ];

// // Phone validation function
// const validatePhoneNumber = (phoneNumber: string, countryCode: string) => {
//   const country = countryCodes.find(c => c.dial === countryCode);
//   if (!country) return { isValid: false, error: "Invalid country code" };
  
//   // Remove all non-digit characters for length validation
//   const digitsOnly = phoneNumber.replace(/\D/g, '');
  
//   // Check if empty
//   if (!phoneNumber.trim()) {
//     return { isValid: true, error: null }; // Allow empty for optional fields
//   }
  
//   // Check pattern
//   if (!country.pattern.test(phoneNumber)) {
//     return { isValid: false, error: "Invalid characters in phone number" };
//   }
  
//   // Check length
//   if (digitsOnly.length < country.minLength) {
//     return { isValid: false, error: `Phone number too short. Minimum ${country.minLength} digits required.` };
//   }
  
//   if (digitsOnly.length > country.maxLength) {
//     return { isValid: false, error: `Phone number too long. Maximum ${country.maxLength} digits allowed.` };
//   }
  
//   return { isValid: true, error: null };
// };

// // Format phone number according to country format
// const formatPhoneNumber = (phoneNumber: string, countryCode: string) => {
//   const country = countryCodes.find(c => c.dial === countryCode);
//   if (!country) return phoneNumber;
  
//   const digitsOnly = phoneNumber.replace(/\D/g, '');
//   let formatted = '';
//   let digitIndex = 0;
  
//   for (let char of country.format) {
//     if (char === 'X' && digitIndex < digitsOnly.length) {
//       formatted += digitsOnly[digitIndex];
//       digitIndex++;
//     } else if (char !== 'X' && digitIndex > 0) {
//       formatted += char;
//     }
//   }
  
//   return formatted;
// };

// // Get placeholder for selected country
// const getPlaceholder = (countryCode: string) => {
//   const country = countryCodes.find(c => c.dial === countryCode);
//   return country ? country.placeholder : "Enter phone number";
// };

// export default function ContactSection() {
//   const router = useRouter();

//   const [formData, setFormData] = useState({
//     name: "",
//     email: "",
//     phone: "",
//     company: "",
//     category: "",
//     message: "",
//     countryCode: "+91",
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [isSuccess, setIsSuccess] = useState(false)
//   const [phoneError, setPhoneError] = useState("")
//   const sectionRef = useRef<HTMLDivElement>(null)
//   const isInView = useInView(sectionRef, { once: false, amount: 0.2, margin: "-100px" })

//   const containerVariants = {
//     hidden: { opacity: 1 },
//     visible: {
//       opacity: 1,
//       transition: {
//         staggerChildren: 0.1,
//       },
//     },
//   }

//   const itemVariants = {
//     hidden: { opacity: 1, y: 20 },
//     visible: {
//       opacity: 1,
//       y: 0,
//       transition: { duration: 0.6, ease: "easeOut" },
//     },
//   }

//   const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleSelectChange = (value: string) => {
//     setFormData((prev) => ({ ...prev, category: value }))
//   }

//   const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
//     const code = e.target.value;
//     setFormData((prev) => ({
//       ...prev,
//       countryCode: code,
//       phone: "" // Clear phone when country changes
//     }));
//     setPhoneError(""); // Clear any existing error
//   };

//   const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
//     const value = e.target.value;
//     const validation = validatePhoneNumber(value, formData.countryCode);
    
//     if (validation.isValid || value === "") {
//       // Auto-format the phone number
//       const formatted = formatPhoneNumber(value, formData.countryCode);
//       setFormData((prev) => ({ ...prev, phone: formatted }));
//       setPhoneError("");
//     } else {
//       setFormData((prev) => ({ ...prev, phone: value }));
//       setPhoneError(validation.error || "");
//     }
//   };

//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault()
    
//     // Validate phone number before submission
//     if (formData.phone) {
//       const validation = validatePhoneNumber(formData.phone, formData.countryCode);
//       if (!validation.isValid) {
//         setPhoneError(validation.error || "Invalid phone number");
//         return;
//       }
//     }
    
//     setIsSubmitting(true)

//     try {
//       const res = await fetch("/api/send-email", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           ...formData,
//           phone: formData.phone ? `${formData.countryCode} ${formData.phone}` : "",
//         }),
//       })

//       const result = await res.json()
//       if (result.success) {
//         setIsSuccess(true)
//         router.push('/thank-you'); // Redirect to thank you page

//         setTimeout(() => {
//           setFormData({ 
//             name: "", 
//             email: "", 
//             phone: "", 
//             company: "", 
//             category: "", 
//             message: "", 
//             countryCode: "+91" 
//           })
//           setIsSuccess(false)
//           setPhoneError("")
//         }, 2000)
//       } else {
//         alert("Failed to send email. Please try again.")
//       }
//     } catch (err) {
//       console.error("Email error", err)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }

//   const reportCategories = [
//     { value: "Annual Reports", label: "Annual Reports" },
//     { value: "Integrated Reports", label: "Integrated Reports" },
//     { value: "Sustainability Reports", label: "Sustainability Reports" },
//     { value: "CSR Reports", label: "CSR Reports" },
//     { value: "Digital Reports", label: "Digital Reports" },
//     { value: "Investor Presentations", label: "Investor Presentations" },
//     { value: "Creative Reports", label: "Creative Reports" },
//   ]

//   return (
//     <section
//       ref={sectionRef}
//       className="py-16 md:py-20 lg:py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
//       id="contact"
//     >
//       <div className="absolute inset-0 overflow-hidden">
//         <div className="absolute top-0 left-0 w-full h-full bg-dots-pattern opacity-5"></div>
//       </div>

//       <div className="container mx-auto px-4 relative z-10 max-w-6xl">
//         <motion.div
//           initial="hidden"
//           animate={isInView ? "visible" : "hidden"}
//           variants={containerVariants}
//           className="text-center mb-8 md:mb-16"
//         >
//           <motion.h2
//             variants={itemVariants}
//             className="inline-block mb-3 md:mb-4 px-4 py-2 rounded-full bg-gray-200 text-gray-800 font-medium text-sm"
//           >
//             Contact Us
//           </motion.h2>

//           <motion.h3
//             variants={itemVariants}
//             className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4"
//           >
//             Join hundreds of forward-thinking companies
//           </motion.h3>

//           <motion.p variants={itemVariants} className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
//             Let's discuss how we can help transform your corporate reporting and create impactful designs that resonate
//             with your stakeholders.
//           </motion.p>
//         </motion.div>

//         <motion.div
//           initial="hidden"
//           animate={isInView ? "visible" : "hidden"}
//           variants={containerVariants}
//           className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 items-start"
//         >
//           {/* Contact Form - 3 columns */}
//           <motion.div variants={itemVariants} className="lg:col-span-3">
//             <motion.div
//               className="bg-white rounded-xl md:rounded-3xl shadow-lg p-6 md:p-8 lg:p-10 relative overflow-hidden border"
//               whileHover={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)" }}
//             >
//               {/* Decorative elements */}
//               <div className="absolute -top-20 -right-20 w-40 h-40 bg-gray-200/50 rounded-full"></div>
//               <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gray-300/30 rounded-full"></div>

//               <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 relative z-10">
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
//                   <div>
//                     <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
//                       Name <span className="text-red-500">*</span>
//                     </label>
//                     <Input
//                       id="name"
//                       name="name"
//                       value={formData.name}
//                       onChange={handleChange}
//                       className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
//                       placeholder="Your name"
//                       required
//                     />
//                   </div>

//                   <div>
//                     <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
//                       Email <span className="text-red-500">*</span>
//                     </label>
//                     <Input
//                       id="email"
//                       name="email"
//                       type="email"
//                       value={formData.email}
//                       onChange={handleChange}
//                       className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
//                       placeholder="your.email@company.com"
//                       required
//                     />
//                   </div>
//                 </div>

//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
//                   <div>
//                     <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
//                       Phone Number
//                     </label>
//                     <div className="flex gap-2">
//                       <select
//                         value={formData.countryCode}
//                         onChange={handleCountryCodeChange}
//                         className="bg-gray-50 border border-gray-300 rounded-lg md:rounded-xl h-12 px-3 min-w-[100px]"
//                       >
//                         {countryCodes.map((country) => (
//                           <option key={country.code} value={country.dial}>
//                             {country.dial} {country.code}
//                           </option>
//                         ))}
//                       </select>

//                       <div className="flex-1">
//                         <Input
//                           id="phone"
//                           name="phone"
//                           value={formData.phone}
//                           onChange={handlePhoneChange}
//                           className={`bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12 ${
//                             phoneError ? 'border-red-500 focus:border-red-500' : ''
//                           }`}
//                           placeholder={getPlaceholder(formData.countryCode)}
//                         />
//                       </div>
//                     </div>
//                     {phoneError && (
//                       <p className="text-red-500 text-sm mt-1">{phoneError}</p>
//                     )}
//                   </div>

//                   <div>
//                     <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
//                       Company
//                     </label>
//                     <Input
//                       id="company"
//                       name="company"
//                       value={formData.company}
//                       onChange={handleChange}
//                       className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
//                       placeholder="Your company name"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label htmlFor="category" className="block text-gray-700 font-medium mb-2">
//                     Report Type
//                   </label>
//                   <Select value={formData.category} onValueChange={handleSelectChange}>
//                     <SelectTrigger className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12">
//                       <SelectValue placeholder="Select a report type" />
//                     </SelectTrigger>
//                     <SelectContent className="rounded-lg md:rounded-xl">
//                       {reportCategories.map((category) => (
//                         <SelectItem key={category.value} value={category.value}>
//                           {category.label}
//                         </SelectItem>
//                       ))}
//                     </SelectContent>
//                   </Select>
//                 </div>

//                 <div>
//                   <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
//                     Message <span className="text-red-500">*</span>
//                   </label>
//                   <Textarea
//                     id="message"
//                     name="message"
//                     value={formData.message}
//                     onChange={handleChange}
//                     rows={5}
//                     className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl resize-none"
//                     placeholder="Tell us about your project and requirements..."
//                     required
//                   />
//                 </div>

//                 <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
//                   <button
//                     type="submit"
//                     disabled={isSubmitting || isSuccess || !!phoneError}
//                     className={`w-full rounded-lg md:rounded-xl py-4 md:py-6 text-base md:text-lg font-medium flex items-center justify-center gap-2 transition-all duration-300 ${
//                       isSuccess 
//                         ? "bg-green-500 hover:bg-green-600" 
//                         : phoneError 
//                         ? "bg-gray-400 cursor-not-allowed"
//                         : "bg-gray-800 hover:bg-gray-900"
//                     } text-white`}
//                   >
//                     {isSubmitting ? (
//                       <>
//                         <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
//                         <span>Sending...</span>
//                       </>
//                     ) : isSuccess ? (
//                       <>
//                         <svg
//                           className="w-5 h-5"
//                           fill="none"
//                           stroke="currentColor"
//                           viewBox="0 0 24 24"
//                           xmlns="http://www.w3.org/2000/svg"
//                         >
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                         </svg>
//                         <span>Message Sent!</span>
//                       </>
//                     ) : (
//                       <>
//                         <Send className="w-5 h-5" />
//                         <span>Send Message</span>
//                       </>
//                     )}
//                   </button>
//                 </motion.div>
//               </form>
//             </motion.div>
//           </motion.div>

//           {/* Contact Information - 2 columns */}
//           <motion.div variants={itemVariants} className="lg:col-span-2">
//             <div className="bg-gray-800 text-white rounded-xl md:rounded-3xl shadow-lg p-6 md:p-8 h-full">
//               <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-white">Contact Information</h3>

//               <div className="space-y-6 md:space-y-8">
//                 <div className="flex items-start">
//                   <div className="bg-white/20 p-3 rounded-full mr-4">
//                     <MapPin className="w-5 h-5 md:w-6 md:h-6 text-white" />
//                   </div>
//                   <div>
//                     <h4 className="text-white font-medium mb-2">Our Address</h4>
//                     <p className="text-gray-300 text-sm md:text-base">
//                       203, BSI Business Park, H-160, H Block, Sector 63, Noida, Uttar Pradesh 201301, India.
//                     </p>
//                   </div>
//                 </div>

//                 <div className="flex items-start">
//                   <div className="bg-white/20 p-3 rounded-full mr-4">
//                     <Phone className="w-5 h-5 md:w-6 md:h-6 text-white" />
//                   </div>
//                   <div>
//                     <h4 className="text-white font-medium mb-2">Call Us</h4>
//                     <a href="tel:+919411953348" className="text-lg md:text-xl font-bold text-white">+91 94119 53348 (India)</a>
//                   </div>
//                 </div>

//                 <div className="flex items-start">
//                   <div className="bg-white/20 p-3 rounded-full mr-4">
//                     <Mail className="w-5 h-5 md:w-6 md:h-6 text-white" />
//                   </div>
//                   <div>
//                     <h4 className="text-white font-medium mb-2">Email Us</h4>
//                     <a href="mailto:contact@annualesg.com" className="text-lg md:text-xl font-bold text-white">contact@annualesg.com</a>
//                   </div>
//                 </div>

//                 <div className="flex items-start">
//                   <div className="bg-white/20 p-3 rounded-full mr-4">
//                     <Clock className="w-5 h-5 md:w-6 md:h-6 text-white" />
//                   </div>
//                   <div>
//                     <h4 className="text-white font-medium mb-2">Business Hours</h4>
//                     <p className="text-gray-300 text-sm md:text-base">Monday - Saturday, 9:00 AM - 6:00 PM</p>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </motion.div>
//         </motion.div>
//       </div>
//     </section>
//   )
// }














// "use client"

// import type React from "react"

// import { useRef, useState } from "react"
// import { motion, useInView } from "framer-motion"
// import { Send, MapPin, Phone, Mail, Clock } from "lucide-react"
// import { Input } from "@/components/ui/input"
// import { Textarea } from "@/components/ui/textarea"
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
// import { useRouter } from 'next/navigation';

// export default function ContactSection() {
//   const router = useRouter();

//   const [formData, setFormData] = useState({
//     name: "",
//     email: "",
//     phone: "",
//     company: "",
//     category: "",
//     message: "",
//     countryCode: "",
//   })
//   const [isSubmitting, setIsSubmitting] = useState(false)
//   const [isSuccess, setIsSuccess] = useState(false)
//   const sectionRef = useRef<HTMLDivElement>(null)
//   const isInView = useInView(sectionRef, { once: false, amount: 0.2, margin: "-100px" })

//   const containerVariants = {
//     hidden: { opacity: 1 },
//     visible: {
//       opacity: 1,
//       transition: {
//         staggerChildren: 0.1,
//       },
//     },
//   }

//   const itemVariants = {
//     hidden: { opacity: 1, y: 20 },
//     visible: {
//       opacity: 1,
//       y: 0,
//       transition: { duration: 0.6, ease: "easeOut" },
//     },
//   }

//   const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
//     const { name, value } = e.target
//     setFormData((prev) => ({ ...prev, [name]: value }))
//   }

//   const handleSelectChange = (value: string) => {
//     setFormData((prev) => ({ ...prev, category: value }))
//   }



//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault()
//     setIsSubmitting(true)


//     try {
//       const res = await fetch("/api/send-email", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//     ...formData,
//     phone: `${formData.countryCode}-${formData.phone}`,
//   }),
//       })

//       const result = await res.json()
//       if (result.success) {
//         setIsSuccess(true)
//         router.push('/thank-you'); // Redirect to thank you page

//         setTimeout(() => {
//           setFormData({ name: "", email: "", phone: "", company: "", category: "", message: "", countryCode: "" })
//           setIsSuccess(false)
//         }, 2000)
//       } else {
//         alert("Failed to send email. Please try again.")
//       }
//     } catch (err) {
//       console.error("Email error", err)
//     } finally {
//       setIsSubmitting(false)
//     }
//   }




//   const reportCategories = [
//     { value: "Annual Reports", label: "Annual Reports" },
//     { value: "Integrated Reports", label: "Integrated Reports" },
//     { value: "Sustainability Reports", label: "Sustainability Reports" },
//     { value: "CSR Reports", label: "CSR Reports" },
//     { value: "Digital Reports", label: "Digital Reports" },
//     { value: "Investor Presentations", label: "Investor Presentations" },
//     { value: "Creative Reports", label: "Creative Reports" },
//   ]





//   const [selectedCode, setSelectedCode] = useState("+91");

//   const countryCodes = [
//     { code: "US", name: "United States", dial: "+1" },
//     { code: "IN", name: "India", dial: "+91" },
//     { code: "GB", name: "United Kingdom", dial: "+44" },
//     { code: "AU", name: "Australia", dial: "+61" },
//     { code: "DE", name: "Germany", dial: "+49" },
//     { code: "FR", name: "France", dial: "+33" },
//     { code: "JP", name: "Japan", dial: "+81" },
//     { code: "CN", name: "China", dial: "+86" },
//     // Add more as needed
//   ];




//   const handleSelectChangeCountryCode = (e: { target: { value: any } }) => {
//     const code = e.target.value;
//     setSelectedCode(code);
//     // Optional: update the phone input as well
//     setFormData((prev) => ({
//       ...prev,
//       countryCode: code,
//     }));
//   };

//   interface PhoneInputEvent extends React.ChangeEvent<HTMLInputElement> { }

//   const handleInputChange = (e: PhoneInputEvent) => {
//     setFormData((prev) => ({
//       ...prev,
//       phone: e.target.value,
//     }));
//   };

//   return (
//     <section
//       ref={sectionRef}
//       className="py-16 md:py-20 lg:py-32 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
//       id="contact"
//     >
//       <div className="absolute inset-0 overflow-hidden">
//         <div className="absolute top-0 left-0 w-full h-full bg-dots-pattern opacity-5"></div>
//       </div>

//       <div className="container mx-auto px-4 relative z-10 max-w-6xl">
//         <motion.div
//           initial="hidden"
//           animate={isInView ? "visible" : "hidden"}
//           variants={containerVariants}
//           className="text-center mb-8 md:mb-16"
//         >
//           <motion.h2
//             variants={itemVariants}
//             className="inline-block mb-3 md:mb-4 px-4 py-2 rounded-full bg-gray-200 text-gray-800 font-medium text-sm"
//           >
//             Contact Us
//           </motion.h2>

//           <motion.h3
//             variants={itemVariants}
//             className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 md:mb-4"
//           >
//             Join hundreds of forward-thinking companies
//           </motion.h3>

//           <motion.p variants={itemVariants} className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto">
//             Let's discuss how we can help transform your corporate reporting and create impactful designs that resonate
//             with your stakeholders.
//           </motion.p>
//         </motion.div>

//         <motion.div
//           initial="hidden"
//           animate={isInView ? "visible" : "hidden"}
//           variants={containerVariants}
//           className="grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12 items-start"
//         >
//           {/* Contact Form - 3 columns */}
//           <motion.div variants={itemVariants} className="lg:col-span-3">
//             <motion.div
//               className="bg-white rounded-xl md:rounded-3xl shadow-lg p-6 md:p-8 lg:p-10 relative overflow-hidden border"
//               whileHover={{ boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)" }}
//             >
//               {/* Decorative elements */}
//               <div className="absolute -top-20 -right-20 w-40 h-40 bg-gray-200/50 rounded-full"></div>
//               <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gray-300/30 rounded-full"></div>

//               <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6 relative z-10">
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
//                   <div>
//                     <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
//                       Name <span className="text-red-500">*</span>
//                     </label>
//                     <Input
//                       id="name"
//                       name="name"
//                       value={formData.name}
//                       onChange={handleChange}
//                       className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
//                       placeholder="Your name"
//                       required
//                     />
//                   </div>

//                   <div>
//                     <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
//                       Email <span className="text-red-500">*</span>
//                     </label>
//                     <Input
//                       id="email"
//                       name="email"
//                       type="email"
//                       value={formData.email}
//                       onChange={handleChange}
//                       className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
//                       placeholder="your.email@company.com"
//                       required
//                     />
//                   </div>
//                 </div>

//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
//                   <div>
//                     <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
//                       Phone Number
//                     </label>
//                     {/* <Input
//                       id="phone"
//                       name="phone"
//                       value={formData.phone}
//                       onChange={handleChange}
//                       className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
//                       placeholder="+1 (555) 000-0000"
//                     /> */}

//                     <div className="flex gap-2">
//                       <select
//                         value={selectedCode}
//                         onChange={handleSelectChangeCountryCode}
//                         className="bg-gray-50 border border-gray-300 rounded-lg md:rounded-xl h-12 px-2"
//                       >
//                         {countryCodes.map((country) => (
//                           <option key={country.code} value={country.dial}>
//                             ({country.dial})
//                           </option>
//                         ))}
//                       </select>

//                       <Input
//                         id="phone"
//                         name="phone"
//                         value={formData.phone}
//                         onChange={handleInputChange}
//                         className="flex-1 bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
//                         placeholder={`(555) 000-0000`}
//                       />
//                     </div>
//                   </div>


//                   <div>
//                     <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
//                       Company
//                     </label>
//                     <Input
//                       id="company"
//                       name="company"
//                       value={formData.company}
//                       onChange={handleChange}
//                       className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12"
//                       placeholder="Your company name"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label htmlFor="category" className="block text-gray-700 font-medium mb-2">
//                     Report Type
//                   </label>
//                   <Select value={formData.category} onValueChange={handleSelectChange}>
//                     <SelectTrigger className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl h-12">
//                       <SelectValue placeholder="Select a report type" />
//                     </SelectTrigger>
//                     <SelectContent className="rounded-lg md:rounded-xl">
//                       {reportCategories.map((category) => (
//                         <SelectItem key={category.value} value={category.value}>
//                           {category.label}
//                         </SelectItem>
//                       ))}
//                     </SelectContent>
//                   </Select>
//                 </div>

//                 <div>
//                   <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
//                     Message <span className="text-red-500">*</span>
//                   </label>
//                   <Textarea
//                     id="message"
//                     name="message"
//                     value={formData.message}
//                     onChange={handleChange}
//                     rows={5}
//                     className="bg-gray-50 border-gray-300 focus:border-gray-700 rounded-lg md:rounded-xl resize-none"
//                     placeholder="Tell us about your project and requirements..."
//                     required
//                   />
//                 </div>

//                 <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
//                   <button
//                     type="submit"
//                     disabled={isSubmitting || isSuccess}
//                     className={`w-full rounded-lg md:rounded-xl py-4 md:py-6 text-base md:text-lg font-medium flex items-center justify-center gap-2 transition-all duration-300 ${isSuccess ? "bg-green-500 hover:bg-green-600" : "bg-gray-800 hover:bg-gray-900"
//                       } text-white`}
//                   >
//                     {isSubmitting ? (
//                       <>
//                         <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
//                         <span>Sending...</span>
//                       </>
//                     ) : isSuccess ? (
//                       <>
//                         <svg
//                           className="w-5 h-5"
//                           fill="none"
//                           stroke="currentColor"
//                           viewBox="0 0 24 24"
//                           xmlns="http://www.w3.org/2000/svg"
//                         >
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                         </svg>
//                         <span>Message Sent!</span>
//                       </>
//                     ) : (
//                       <>
//                         <Send className="w-5 h-5" />
//                         <span>Send Message</span>
//                       </>
//                     )}
//                   </button>
//                 </motion.div>
//               </form>
//             </motion.div>
//           </motion.div>

//           {/* Contact Information - 2 columns */}
//           <motion.div variants={itemVariants} className="lg:col-span-2">
//             <div className="bg-gray-800 text-white rounded-xl md:rounded-3xl shadow-lg p-6 md:p-8 h-full">
//               <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-white">Contact Information</h3>

//               <div className="space-y-6 md:space-y-8">
//                 <div className="flex items-start">
//                   <div className="bg-white/20 p-3 rounded-full mr-4">
//                     <MapPin className="w-5 h-5 md:w-6 md:h-6 text-white" />
//                   </div>
//                   <div>
//                     <h4 className="text-white font-medium mb-2">Our Address</h4>
//                     <p className="text-gray-300 text-sm md:text-base">
//                       203, BSI Business Park, H-160, H Block, Sector 63, Noida, Uttar Pradesh 201301, India.
//                     </p>
//                   </div>
//                 </div>

//                 <div className="flex items-start">
//                   <div className="bg-white/20 p-3 rounded-full mr-4">
//                     <Phone className="w-5 h-5 md:w-6 md:h-6 text-white" />
//                   </div>
//                   <div>
//                     <h4 className="text-white font-medium mb-2">Call Us</h4>
//                     <a href="tel:+919411953348" className="text-lg md:text-xl font-bold text-white">+91 94119 53348 (India)</a>
//                   </div>
//                 </div>

//                 <div className="flex items-start">
//                   <div className="bg-white/20 p-3 rounded-full mr-4">
//                     <Mail className="w-5 h-5 md:w-6 md:h-6 text-white" />
//                   </div>
//                   <div>
//                     <h4 className="text-white font-medium mb-2">Email Us</h4>
//                     <a href="mailto:contact@annualesg.com" className="text-lg md:text-xl font-bold text-white">contact@annualesg.com</a>
//                   </div>
//                 </div>

//                 <div className="flex items-start">
//                   <div className="bg-white/20 p-3 rounded-full mr-4">
//                     <Clock className="w-5 h-5 md:w-6 md:h-6 text-white" />
//                   </div>
//                   <div>
//                     <h4 className="text-white font-medium mb-2">Business Hours</h4>
//                     <p className="text-gray-300 text-sm md:text-base">Monday - Saturday, 9:00 AM - 6:00 PM</p>
//                   </div>
//                 </div>
//               </div>


//             </div>
//           </motion.div>

//         </motion.div>
//       </div>
//     </section>
//   )
// }
